import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import {
  QrCode2,
  CurrencyRupee,
  ShowChart,
  EventSeat,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import FloatingBlobs from '../components/FloatingBlobs';

const WA_URL =
  'https://wa.me/918595675155?text=Hi%20BookMyLibrary%2C%20I%27m%20interested%20in%20listing%20my%20library';

const ROTATING_WORDS = ['Grow', 'Scale', 'Automate', 'Digitize'];
const ROTATE_INTERVAL_MS = 2400;

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: 'easeOut' },
});

/**
 * Cycles through ROTATING_WORDS with a fade-slide transition.
 */
function RotatingWord({ orange, golden }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      component="span"
      sx={{ display: 'inline-block', position: 'relative', minWidth: '3ch' }}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={ROTATING_WORDS[index]}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          style={{
            display: 'inline-block',
            background: `linear-gradient(135deg, ${orange}, ${golden})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
          }}
        >
          {ROTATING_WORDS[index]}
        </motion.span>
      </AnimatePresence>
    </Box>
  );
}

RotatingWord.propTypes = {
  orange: PropTypes.string.isRequired,
  golden: PropTypes.string.isRequired,
};

/**
 * Seat grid mock — 8 columns × 4 rows of tiny squares.
 */
function SeatGrid({ orange, grey }) {
  const seats = Array.from({ length: 32 }, (_, i) => i);
  const filled = new Set([0, 1, 2, 4, 5, 8, 9, 10, 16, 17, 20, 24]);
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 1fr)',
        gap: 0.5,
      }}
      aria-label="Library seat availability grid"
    >
      {seats.map((i) => (
        <Box
          key={i}
          sx={{
            width: 18,
            height: 18,
            borderRadius: '4px',
            bgcolor: filled.has(i) ? orange : grey,
            transition: 'background 0.2s',
          }}
        />
      ))}
    </Box>
  );
}

SeatGrid.propTypes = {
  orange: PropTypes.string.isRequired,
  grey: PropTypes.string.isRequired,
};

/**
 * Dashboard card mock displayed in the hero right column.
 */
function DashboardMock({ totalCount }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;
  const navy = theme.palette.brand.navy.main;

  const cardBg = isDark ? 'rgba(27,54,93,0.9)' : '#fff';
  const greySquare = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(27,54,93,0.12)';

  const liveLine =
    totalCount > 0 ? `${totalCount}+ libraries live` : 'Join a growing network';

  return (
    <Box
      sx={{
        background: cardBg,
        borderRadius: '24px',
        p: 3,
        boxShadow: isDark
          ? '0 24px 64px rgba(0,0,0,0.5)'
          : '0 24px 64px rgba(27,54,93,0.18)',
        border: isDark
          ? '1px solid rgba(255,255,255,0.1)'
          : '1px solid rgba(27,54,93,0.08)',
        maxWidth: 380,
        mx: 'auto',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Seat Overview
        </Typography>
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: '999px',
            background: `linear-gradient(135deg, ${orange}, ${golden})`,
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 700,
          }}
        >
          LIVE
        </Box>
      </Box>

      {/* Seat grid */}
      <SeatGrid orange={orange} grey={greySquare} />

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: orange }} />
          <Typography variant="caption" color="text.secondary">Occupied</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: greySquare }} />
          <Typography variant="caption" color="text.secondary">Available</Typography>
        </Box>
      </Box>

      {/* Ready to earn tile */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          borderRadius: '14px',
          background: isDark
            ? 'rgba(242,101,34,0.12)'
            : 'rgba(242,101,34,0.06)',
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            background: `linear-gradient(135deg, ${orange}, ${golden})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CurrencyRupee sx={{ color: '#fff', fontSize: 20 }} />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Your earning potential
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1 }}>
            Ready to earn
          </Typography>
        </Box>
        <ShowChart sx={{ ml: 'auto', color: orange, opacity: 0.7, fontSize: 28 }} />
      </Box>

      {/* Libraries live chip + bottom row */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Box
          sx={{
            flex: 1,
            p: 1.5,
            borderRadius: '12px',
            background: isDark
              ? 'rgba(27,54,93,0.5)'
              : 'rgba(27,54,93,0.04)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <QrCode2 sx={{ color: navy, fontSize: 28 }} />
          <Typography variant="caption" color="text.secondary" align="center">
            QR Check-in
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 2,
            p: 1.5,
            borderRadius: '12px',
            background: isDark
              ? 'rgba(27,54,93,0.5)'
              : 'rgba(27,54,93,0.04)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {/* Pulsing green dot */}
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#22c55e',
              flexShrink: 0,
              '@keyframes libraryPulse': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.5, transform: 'scale(1.4)' },
              },
              animation: 'libraryPulse 2s ease-in-out infinite',
            }}
            aria-hidden="true"
          />
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.2 }}
            >
              {liveLine}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <EventSeat sx={{ color: navy, fontSize: 14 }} />
              <Typography variant="caption" color="text.secondary">
                Seats
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

DashboardMock.propTypes = {
  totalCount: PropTypes.number,
};

DashboardMock.defaultProps = {
  totalCount: 0,
};

/**
 * Two-column hero section with floating blobs and rotating headline word.
 */
function HeroSection({ totalCount }) {
  const theme = useTheme();
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const handleScrollToSignup = () => {
    const el = document.getElementById('signup');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: { xs: 'auto', md: '90vh' },
        display: 'flex',
        alignItems: 'center',
        py: { xs: 8, md: 4 },
      }}
    >
      {/* Floating background blobs — behind content */}
      <FloatingBlobs variant="hero" />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          {/* Left column */}
          <Grid item xs={12} md={6}>
            <motion.div {...fadeUp(0)}>
              <Typography
                variant="overline"
                sx={{
                  color: orange,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  fontSize: '0.75rem',
                  mb: 1,
                  display: 'block',
                }}
              >
                For library owners
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.1)}>
              <Typography
                component="h1"
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.25rem', md: '3.5rem' },
                  lineHeight: 1.1,
                  color: 'text.primary',
                  mb: 2.5,
                  letterSpacing: '-0.02em',
                }}
              >
                Fill every seat.{' '}
                <RotatingWord orange={orange} golden={golden} />{' '}
                <Box component="span" sx={{ color: 'text.primary' }}>
                  your library.
                </Box>
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.2)}>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  lineHeight: 1.75,
                  mb: 4,
                  maxWidth: 480,
                }}
              >
                BookMyLibrary helps study libraries across India get discovered by
                students, fill seats with upfront digital bookings, accept payments
                seamlessly, and automate daily attendance — all from one mobile-first
                platform.
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.3)}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleScrollToSignup}
                  sx={{
                    background: `linear-gradient(135deg, ${orange}, ${golden})`,
                    color: '#fff',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: 3,
                    boxShadow: '0 8px 24px rgba(242,101,34,0.35)',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.brand.orange.light}, ${orange})`,
                      boxShadow: '0 12px 32px rgba(242,101,34,0.45)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  List your library
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component="a"
                  href={WA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderColor: orange,
                    color: orange,
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: 3,
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: orange,
                      borderWidth: 2,
                      background: 'rgba(242,101,34,0.06)',
                    },
                  }}
                >
                  Chat on WhatsApp
                </Button>
              </Box>
            </motion.div>
          </Grid>

          {/* Right column — dashboard mock */}
          <Grid item xs={12} md={6}>
            <motion.div {...fadeUp(0.15)}>
              <DashboardMock totalCount={totalCount} />
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

HeroSection.propTypes = {
  totalCount: PropTypes.number,
};

HeroSection.defaultProps = {
  totalCount: 0,
};

export default HeroSection;
